/**
 * ============================================================================
 *  MODULE ACCESS CONFIG
 * ============================================================================
 *  Single source of truth for *module level* access control of the three
 *  "restricted" account roles:
 *
 *      admin-restricted      created by an  admin           (Organization Owner)
 *      agency-restricted     created by an  agency-admin    (Agency Owner)
 *      provider-restricted   created by a   provider-admin  (Provider Owner)
 *
 *  An owner picks the modules a restricted user may use in
 *  Account -> Users -> Add/Edit user -> "Allowed Modules".
 *
 *  Module flags are stored EXACTLY like the existing permission checkboxes:
 *  flat, one boolean per checkbox name, in the `user_permission.rights` JSON
 *  column. There is no separate namespace - a module key IS a rights key.
 *
 *      rights = {
 *        displays: true,          // <- module: shows "Displays" in the sidebar
 *        ipBlackList: false,      // <- module: hidden and route blocked
 *        logs: true,              // <- module
 *        managePlayerGroup: true, // <- action level right (Permissions section)
 *        manageApks: false,       // <- action level right
 *        ...
 *      }
 *
 *  That is why several module keys deliberately reuse names that already
 *  existed in the Permissions section (displays, ipBlackList, gpsConfigurations,
 *  ibus, urlCreator, stopsControl, dasAvalibleMessages, gtfsStopMonitoring,
 *  gtfsAgencyConfig, serverModule): a user who already had those flags set
 *  keeps working with no migration. Those checkboxes were removed from the
 *  Permissions section so each flag has exactly one owner in the UI.
 *
 *  Because `rights` is a JSON column, no backend/DB change is required.
 *
 *  This single table then drives THREE things, so they can never drift apart:
 *    1. the checkbox list rendered in the Account User form
 *    2. the left sidebar (app/store/fuse/navigationSlice.js)
 *    3. the route guard (@fuse/core/FuseAuthorization)
 *
 *  ---------------------------------------------------------------------------
 *  HOW TO ADD A NEW MODULE
 *  ---------------------------------------------------------------------------
 *  Add one entry to MODULES below with:
 *     key    - the checkbox name / rights key (never rename an existing one)
 *     label  - text shown next to the checkbox
 *     group  - heading it is rendered under in the form
 *     owners - which owner roles are allowed to grant it
 *     paths  - EVERY route prefix that belongs to the module. Anything that
 *              starts with one of these prefixes is gated by this module.
 *  ============================================================================
 */

/** The three roles that are subject to module level gating. */
export const RESTRICTED_ROLES = ['admin-restricted', 'agency-restricted', 'provider-restricted'];

/** Owner role -> the restricted role it creates. */
export const OWNER_TO_RESTRICTED = {
  admin: 'admin-restricted',
  'agency-admin': 'agency-restricted',
  'provider-admin': 'provider-restricted',
};

/**
 * Legacy safety switch.
 *
 * Applies only to restricted users whose `rights` is completely empty (or was
 * never written). Those users would otherwise be left with nothing but their
 * dashboard; while this flag is true they keep their old access until an owner
 * opens them and saves a module selection.
 *
 * A user with ANY rights saved is always treated as authoritative: a module
 * flag that is absent means "not allowed", exactly like the permission
 * checkboxes have always behaved.
 */
export const ALLOW_ALL_WHEN_NO_RIGHTS_SAVED = true;

/**
 * Routes every signed-in user may always reach, whatever their module
 * selection is - landing dashboards, their own profile, the account screen,
 * auth screens and the error pages (the 403 page itself MUST be in here or
 * the redirect would loop).
 *
 * NOTE: 'apps/profile' is the Account screen (where users are managed). If you
 * want restricted users locked out of it, remove it from this list and add it
 * to MODULES as its own entry instead.
 */
export const ALWAYS_ALLOWED_PATHS = [
  'dashboard',
  'dashboards/agency',
  'dashboards/provider',
  'apps/profile',
  'apps/userProfile',
  'pages/error',
  'pages/coming-soon',
  'pages/maintenance',
  'sign-in',
  'sign-up',
  'sign-out',
  'forgot-password',
  'reset-password',
  'verify-2fa',
  'enable-2fa',
  'verify-backup-code',
  'auth/magic-login',
  'auth/check-email',
  'auth/method',
  'loading',
  'documentation',
];

/**
 * The module table. `paths` are matched as prefixes against the current
 * pathname and against each sidebar item's `url`.
 */
export const MODULES = [
  /* ----------------------------- Master Controls ------------------------- */
  {
    key: 'providers',
    label: 'Providers',
    group: 'Master Controls',
    owners: ['admin'],
    paths: ['apps/providers'],
  },
  {
    key: 'agencies',
    label: 'Agencies',
    group: 'Master Controls',
    owners: ['admin', 'provider-admin'],
    paths: ['apps/agencies'],
  },

  /* -------------------------------- Monitoring --------------------------- */
  {
    key: 'displays',
    label: 'Displays',
    group: 'Monitoring',
    owners: ['agency-admin'],
    paths: ['apps/displays'],
  },

  /* ------------------------------ Configurations ------------------------- */
  {
    key: 'ipBlackList',
    label: 'IP Blacklist',
    group: 'Configurations',
    owners: ['agency-admin'],
    paths: ['apps/ip/configurations'],
  },
  {
    key: 'gpsConfigurations',
    label: 'GPS Configuration',
    group: 'Configurations',
    owners: ['agency-admin'],
    paths: ['apps/cradlePoint/configurations'],
  },
  {
    key: 'ibus',
    label: 'IBUS Configuration',
    group: 'Configurations',
    owners: ['agency-admin'],
    paths: ['apps/ibus/configurations'],
  },

  /* --------------------------------- Messages ---------------------------- */
  {
    key: 'urlCreator',
    label: 'URL Builder',
    group: 'Messages',
    owners: ['agency-admin'],
    paths: ['apps/alert-trigger/on-demand'],
  },
  {
    key: 'stopsControl',
    label: 'Stops Control',
    group: 'Messages',
    owners: ['agency-admin'],
    paths: ['apps/stops-control'],
  },

  /* --------------------------------- Logging ----------------------------- */
  {
    key: 'logs',
    label: 'Logs',
    group: 'Logging',
    owners: ['admin', 'agency-admin'],
    paths: ['apps/logs'],
  },

  /* ----------------------------------- DAAS ------------------------------ */
  {
    key: 'daasSimulator',
    label: 'DAAS Simulator',
    group: 'DAAS',
    owners: ['agency-admin'],
    paths: ['apps/daastest'],
  },
  {
    key: 'daasStops',
    label: 'Stops',
    group: 'DAAS',
    owners: ['agency-admin'],
    paths: ['apps/stops', 'apps/bays'],
  },
  {
    key: 'dasAvalibleMessages',
    label: 'Available Messages',
    group: 'DAAS',
    owners: ['agency-admin'],
    paths: ['apps/msgs'],
  },

  /* ----------------------------------- GTFS ------------------------------ */
  {
    key: 'gtfsFeedConfig',
    label: 'Feed Configuration',
    group: 'GTFS',
    owners: ['agency-admin'],
    paths: ['apps/gtfsList'],
    // only offered when the agency itself is entitled to GTFS
    requiresAgencyPermission: 'gtfs',
  },
  {
    key: 'gtfsStopMonitoring',
    label: 'GTFS Monitoring',
    group: 'GTFS',
    owners: ['agency-admin'],
    paths: ['apps/agency/gtfs/monitoring'],
    requiresAgencyPermission: 'gtfs',
  },
  {
    key: 'gtfsAgencyConfig',
    label: 'Agency Configurations',
    group: 'GTFS',
    owners: ['admin'],
    paths: ['apps/gtfs/agency'],
  },
  {
    key: 'gtfsMobility',
    label: 'GTFS Mobility',
    group: 'GTFS',
    owners: ['admin', 'provider-admin'],
    paths: ['apps/mobility'],
  },
  {
    key: 'demoTdm',
    label: 'Demo TDM',
    group: 'GTFS',
    owners: ['admin'],
    paths: ['apps/transitDataManager'],
  },

  /* -------------------------------- Reports ------------------------------ */
  {
    key: 'playerReports',
    label: 'Player Reports',
    group: 'Reports',
    owners: ['admin'],
    paths: ['apps/player-reports'],
  },

  /* ------------------------------ File Manager --------------------------- */
  {
    key: 'apkManager',
    label: 'Apk Manager',
    group: 'File Manager',
    owners: ['admin', 'agency-admin', 'provider-admin'],
    paths: ['apps/apk-manager', 'apps/Jsonapk-manager', 'apps/assign-apk', 'apps/apk-keys-managment'],
  },
  {
    key: 'apkGroups',
    label: 'APK Groups',
    group: 'File Manager',
    owners: ['agency-admin'],
    paths: ['apps/playerGroups', 'apps/players'],
  },
  {
    key: 'quickDownload',
    label: 'Quick Download',
    group: 'File Manager',
    owners: ['admin', 'agency-admin', 'provider-admin'],
    paths: ['apps/prod-apks'],
  },
  {
    key: 'fileManager',
    label: 'File Manager',
    group: 'File Manager',
    owners: ['admin'],
    paths: ['apps/file-manager'],
  },

  /* ------------------------------ Server Manager ------------------------- */
  {
    key: 'serverModule',
    label: 'Server',
    group: 'Server Manager',
    owners: ['admin'],
    paths: ['apps/server-managgment'],
  },
];

/* ==========================================================================
 *  Internals
 * ========================================================================== */

/** `/Apps/Displays/12/` -> `apps/displays/12` (react-router matches case-insensitively) */
function normalizePath(path) {
  if (!path) return '';
  return String(path).trim().replace(/^\/+/, '').replace(/\/+$/, '').toLowerCase();
}

function isPrefixOf(prefix, path) {
  const p = normalizePath(prefix);
  const t = normalizePath(path);
  if (!p) return false;
  return t === p || t.startsWith(`${p}/`);
}

/** Every module key = every module checkbox name. */
export const MODULE_KEYS = MODULES.map((mod) => mod.key);

/** Pre-flattened [prefix, module] pairs, longest prefix first so the most specific wins. */
const PATH_INDEX = MODULES.flatMap((mod) => mod.paths.map((p) => ({ prefix: normalizePath(p), mod }))).sort(
  (a, b) => b.prefix.length - a.prefix.length
);

/** @returns the module a path belongs to, or null when the path is not gated. */
export function resolveModuleForPath(path) {
  const hit = PATH_INDEX.find((entry) => isPrefixOf(entry.prefix, path));
  return hit ? hit.mod : null;
}

export function isAlwaysAllowedPath(path) {
  return ALWAYS_ALLOWED_PATHS.some((p) => isPrefixOf(p, path));
}

export function isRestrictedRole(role) {
  const r = Array.isArray(role) ? role[0] : role;
  return RESTRICTED_ROLES.includes(r);
}

/** Safely read `rights` off the logged-in redux user, whatever shape it arrives in. */
function readRights(user) {
  const raw = user?.data?.userRestrictions?.rights ?? user?.rights ?? null;
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }
  return typeof raw === 'object' ? raw : null;
}

/** Agency level entitlements (`user.permission` is a JSON array such as ["gtfs"]). */
export function getAgencyPermissions(user) {
  const raw = user?.permission ?? user?.data?.permission ?? null;
  if (!raw || raw === 'null') return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
}

/**
 * Build the access descriptor used by the sidebar and the route guard.
 *
 * @returns {{ isRestricted: boolean, allowed: string[], key: string }}
 *          `key` is a cheap value to compare across renders.
 */
export function getModuleAccess(user) {
  const role = Array.isArray(user?.role) ? user?.role[0] : user?.role;

  if (!isRestrictedRole(role)) {
    return { isRestricted: false, allowed: [], key: `open:${role || 'guest'}` };
  }

  const rights = readRights(user);
  // `rights` can legitimately arrive as [] (owners) or {} (never saved)
  const hasAnyRights = rights && typeof rights === 'object' && Object.keys(rights).length > 0;

  if (!hasAnyRights) {
    if (ALLOW_ALL_WHEN_NO_RIGHTS_SAVED) {
      return { isRestricted: false, allowed: [], key: `legacy:${role}` };
    }
    return { isRestricted: true, allowed: [], key: `none:${role}` };
  }

  // module flags live flat in rights, under the same name as the checkbox
  const allowed = MODULE_KEYS.filter((k) => Boolean(rights[k]));
  return { isRestricted: true, allowed, key: `${role}:${allowed.join(',')}` };
}

/**
 * Route guard predicate.
 * Unmapped paths are allowed on purpose - only routes listed in MODULES are
 * gated, so adding a screen never silently locks users out.
 */
export function isPathAllowed(pathname, access) {
  if (!access || !access.isRestricted) return true;
  if (isAlwaysAllowedPath(pathname)) return true;

  const mod = resolveModuleForPath(pathname);
  if (!mod) return true;

  return access.allowed.includes(mod.key);
}

/**
 * Sidebar predicate. Only `type: 'item'` entries are gated - groups and
 * collapses are kept or dropped based on whether any child survived, so a
 * group that happens to carry a `url` never hides its children.
 */
export function isNavItemAllowed(item, access) {
  if (!access || !access.isRestricted) return true;
  if (!item?.url) return true;
  if (item.type && item.type !== 'item' && item.type !== 'link') return true;
  return isPathAllowed(item.url, access);
}

/** Recursively strip sidebar entries the restricted user may not open. */
export function filterNavigationByModules(navigation, access) {
  if (!access || !access.isRestricted) return navigation;

  const walk = (items) =>
    items.reduce((acc, item) => {
      const isContainer = item.type === 'group' || item.type === 'collapse';

      if (isContainer && Array.isArray(item.children)) {
        const children = walk(item.children);
        // drop a container that has been emptied out by the filter
        if (item.children.length > 0 && children.length === 0) return acc;
        acc.push({ ...item, children });
        return acc;
      }

      if (isNavItemAllowed(item, access)) acc.push({ ...item });
      return acc;
    }, []);

  return walk(navigation);
}

/**
 * Modules an owner is allowed to hand out, grouped for the form.
 *
 * @param ownerRole        role of the logged-in owner ('agency-admin', ...)
 * @param agencyPermission agency entitlements array, e.g. ['gtfs']
 * @returns {Array<{ group: string, modules: Array }>}
 */
export function getModuleOptions(ownerRole, agencyPermission = []) {
  const available = MODULES.filter((mod) => {
    if (!mod.owners.includes(ownerRole)) return false;
    if (mod.requiresAgencyPermission && ownerRole === 'agency-admin') {
      return agencyPermission.includes(mod.requiresAgencyPermission);
    }
    return true;
  });

  const groups = [];
  available.forEach((mod) => {
    let bucket = groups.find((g) => g.group === mod.group);
    if (!bucket) {
      bucket = { group: mod.group, modules: [] };
      groups.push(bucket);
    }
    bucket.modules.push(mod);
  });

  return groups;
}

/** Every module key an owner can grant - handy for select all / clear all. */
export function getModuleKeysForOwner(ownerRole, agencyPermission = []) {
  return getModuleOptions(ownerRole, agencyPermission).flatMap((g) => g.modules.map((m) => m.key));
}

/** `{ displays: false, ipBlackList: false, ... }` - used to seed the form state. */
export function getDefaultModuleFlags() {
  return MODULE_KEYS.reduce((acc, key) => {
    acc[key] = false;
    return acc;
  }, {});
}
